import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import DefaultApiLimit from '../../../config/defaultLimit';
import { ApiRequest } from '../../../interface/common.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  fetchConnectionVersionFilters,
  fetchConnectionVersions,
  setSortData,
  updateVersionsFilters,
} from '../../../redux/slices/OCPI/OCPIVersionsSlice';
import { RootState } from '../../../redux/store';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
// import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';

// ── Types ─────────────────────────────────────────────────────────────────────

interface OcpiVersionEntry {
  identifier?: string;
  role?: string;
  version: string;
  url: string;
  _createdAt?: string;
  createdAt?: string;
  created_at?: string;
  created?: string;
}

interface VersionRow {
  id: string;
  identifier: string;
  role: string;
  version: string;
  url: string;
  _createdAt?: string;
  createdAt: string;
  raw: OcpiVersionEntry;
}

// ── Main Panel ────────────────────────────────────────────────────────────────

/**
 * VersionsPanel component displays the OCPI version list cached on the connection
 * between our tenant and the connected partner.
 * @param {any} props The props.
 * @param {any} props.isCpo Whether the active side is a CPO.
 * @param {any} props.partnerCountryCode Partner's country code.
 * @param {any} props.partnerPartyId Partner's party ID.
 */
export default function VersionsPanel({
  isCpo,
  partnerCountryCode,
  partnerPartyId,
}: Readonly<{
  isCpo: boolean;
  partnerCountryCode: string;
  partnerPartyId: string;
}>) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['ocpi']);
  const {
    versions,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    sortBy,
    sortField,
    filters,
    filterListLoading,
  } = useAppSelector((state: RootState) => state.ocpiVersions);

  // Only two OCPI roles exist in this platform, so the counterparty's role is always
  // the opposite of ours.
  const ourRole = isCpo ? 'CPO' : 'EMSP';
  const counterRole = isCpo ? 'EMSP' : 'CPO';

  // ── Data fetching ──────────────────────────────────────────────────────────

  /**
   * Fetches the connection's OCPI version list with the given sort parameters.
   * @param {any} data - The sort parameters.
   */
  const fetchData = useCallback(
    (data: ApiRequest) => {
      if (!loading) {
        dispatch(
          fetchConnectionVersions({
            ...data,
            role: ourRole,
            counterPartyId: partnerPartyId,
            counterCountryCode: partnerCountryCode,
            counterRole,
          }),
        );
      }
    },
    [loading, dispatch, ourRole, counterRole, partnerPartyId, partnerCountryCode],
  );

  useEffect(() => {
    dispatch(
      fetchConnectionVersions({
        role: ourRole,
        counterPartyId: partnerPartyId,
        counterCountryCode: partnerCountryCode,
        counterRole,
      }),
    );
  }, [ourRole, counterRole, partnerPartyId, partnerCountryCode, dispatch]);

  const columns = [
    {
      label: t('Module'),
      accessorKey: 'identifier' as keyof VersionRow,
      sortAllow: false,
      /**
       * Renders the module name in a badge.
       * @param {VersionRow} row The version row.
       */
      customData: (row: VersionRow) => (
        <span className="cpo-tariff-id-badge">{row.identifier}</span>
      ),
    },
    {
      label: t('role'),
      accessorKey: 'role' as keyof VersionRow,
      sortAllow: false,
      /**
       * Renders the role in a badge.
       * @param {VersionRow} row The version row.
       */
      customData: (row: VersionRow) => <span className="cpo-tariff-id-badge">{row.role}</span>,
    },
    {
      label: t('ocpi:url'),
      accessorKey: 'url' as keyof VersionRow,
      sortAllow: false,
    },
  ];

  const tableData: VersionRow[] = versions.map((entry: OcpiVersionEntry) => {
    const createdAt =
      entry._createdAt ||
      entry.createdAt ||
      (entry as any).created_at ||
      (entry as any).created ||
      '';

    return {
      id: entry.identifier
        ? `${entry.identifier}_${entry.role || ''}_${entry.url}`
        : entry.version || entry.url,
      identifier: entry.identifier || '–',
      role: entry.role || '–',
      version: entry.version || '–',
      url: entry.url || '–',
      _createdAt: createdAt,
      createdAt,
      raw: entry,
    };
  });

  /**
   * Table pagination function
   * @param {any} value - The value
   */
  const pageAndLimitChange = (value: ApiRequest) => {
    fetchData({ ...value, sortField: sortField ?? undefined, sortBy: sortBy ?? undefined });
  };

  /**
   * Handle the table's sort change.
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: { sortField: string; sortBy: 'asc' | 'desc' }) => {
    dispatch(setSortData(sortData));
    fetchData({
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  return (
    <div className="cpo-tariffs-panel">
      {/* ── Top bar ── */}
      <div className="cpo-tariffs-topbar">
        <div>
          <h3 className="cpo-panel-title">{t('ocpi:ocpiVersions')}</h3>
          <p className="cpo-panel-subtitle">
            {versions.length > 0
              ? `${versions.length} ${t(
                  versions.length !== 1 ? 'ocpi:versionCountPlural' : 'ocpi:versionCountSingular',
                )} ${t('ocpi:inOcpiGateway')}`
              : t('ocpi:versionsEmptySubtitle')}
          </p>
        </div>
      </div>

      {/* ── Versions Table ── */}
      <CustomTable
        version="v2"
        header={columns}
        data={tableData}
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading}
        serialNo
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageAndLimitChange}
        filters={filters}
        filtersToDispatch={updateVersionsFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchConnectionVersionFilters}
        sizeChangeButtonRequired
        refershCurrentPages={handleRefresh}
        requiredRefreshToggle
        refreshFunction={handleRefresh}
        refreshRequired
        handleRefresh={handleRefresh}
        hoverLabel={t('ocpi:refresh')}
      />
    </div>
  );
}
